import { Injectable } from '@angular/core';
import { FiscalMonthDTO } from '@libs/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface FiscalCalendarData {
  months: FiscalMonthDTO[];
  quarters: Record<string, { startDate: string; endDate: string }>;
  years: Record<string, { startDate: string; endDate: string }>;
}

export interface FiscalWeek {
  weekNumber: number;
  year: number;
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
}

export interface PresetOption {
  label: string;
  value: string;
}

export interface PresetDateRange {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
}

@Injectable({ providedIn: 'root' })
export class FiscalDatePresetService {
  private calendarData: FiscalCalendarData | null = null;
  private fiscalWeeks: FiscalWeek[] = [];

  /** Stores fiscal calendar API data and derives fiscal weeks from months. */
  setCalendarData(data: FiscalCalendarData): void {
    this.calendarData = data;
    this.fiscalWeeks = this.deriveWeeks(data.months);
  }

  readonly datePresetOptions: readonly PresetOption[] = [
    { label: 'Last 7 Days', value: 'last_7_days' },
    { label: 'Last 30 Days', value: 'last_30_days' },
    { label: 'Last 90 Days', value: 'last_90_days' },
    { label: 'Last Fiscal Week', value: 'last_fiscal_week' },
    { label: 'Last 52 Fiscal Weeks', value: 'last_52_fiscal_weeks' },
    { label: 'Last Fiscal Month', value: 'last_fiscal_month' },
    { label: 'Last Fiscal Quarter', value: 'last_fiscal_quarter' },
    { label: 'Last Fiscal Year', value: 'last_fiscal_year' },
    { label: 'This Fiscal Week', value: 'this_fiscal_week' },
    { label: 'This Fiscal Month', value: 'this_fiscal_month' },
    { label: 'This Fiscal Quarter', value: 'this_fiscal_quarter' },
    { label: 'This Fiscal Year', value: 'this_fiscal_year' },
    { label: 'Custom', value: 'custom' }
  ];

  readonly comparePresetOptions: readonly PresetOption[] = [
    { label: 'Previous period', value: 'previous_period' },
    {
      label: 'Previous period (match day of week)',
      value: 'previous_period_match_dow'
    },
    { label: 'Previous fiscal year', value: 'previous_fiscal_year' },
    {
      label: 'Previous fiscal year (match day of week)',
      value: 'previous_fiscal_year_match_dow'
    },
    { label: 'Custom', value: 'custom' }
  ];

  calculateDateRange(presetValue: string): PresetDateRange | null {
    const today = dayjs().utc(true);

    switch (presetValue) {
      case 'last_7_days':
        return this.lastNDays(today, 7);
      case 'last_30_days':
        return this.lastNDays(today, 30);
      case 'last_90_days':
        return this.lastNDays(today, 90);
      case 'last_fiscal_week':
        return this.lastFiscalWeek(today);
      case 'last_52_fiscal_weeks':
        return this.last52FiscalWeeks(today);
      case 'last_fiscal_month':
        return this.lastFiscalMonth(today);
      case 'last_fiscal_quarter':
        return this.lastFiscalQuarter(today);
      case 'last_fiscal_year':
        return this.lastFiscalYear(today);
      case 'this_fiscal_week':
        return this.thisFiscalWeek(today);
      case 'this_fiscal_month':
        return this.thisFiscalMonth(today);
      case 'this_fiscal_quarter':
        return this.thisFiscalQuarter(today);
      case 'this_fiscal_year':
        return this.thisFiscalYear(today);
      case 'custom':
      default:
        return null;
    }
  }

  calculateCompareDateRange(
    presetValue: string,
    currentRange: PresetDateRange
  ): PresetDateRange | null {
    switch (presetValue) {
      case 'previous_period':
        return this.previousPeriod(currentRange);
      case 'previous_period_match_dow':
        return this.previousPeriodMatchDow(currentRange);
      case 'previous_fiscal_year':
        return this.previousFiscalYear(currentRange);
      case 'previous_fiscal_year_match_dow':
        return this.previousFiscalYearMatchDow(currentRange);
      case 'custom':
      default:
        return null;
    }
  }

  /**
   * Last N calendar days: (today - N) to (today - 1).
   */
  private lastNDays(today: dayjs.Dayjs, n: number): PresetDateRange {
    return {
      startDate: today.subtract(n, 'day'),
      endDate: today.subtract(1, 'day')
    };
  }

  /**
   * Last Fiscal Week: find the fiscal week containing today, then go back 1.
   */
  private lastFiscalWeek(today: dayjs.Dayjs): PresetDateRange | null {
    const currentWeek = this.findFiscalWeekForDate(today);
    if (!currentWeek) return null;

    const previousWeek = this.findPreviousFiscalWeek(currentWeek);
    if (!previousWeek) return null;

    return { startDate: previousWeek.startDate, endDate: previousWeek.endDate };
  }

  /**
   * Last 52 Fiscal Weeks: excludes the current week.
   * Find the current fiscal week, go back 1 to get the last completed week,
   * then count 52 weeks ending at that completed week.
   */
  private last52FiscalWeeks(today: dayjs.Dayjs): PresetDateRange | null {
    const currentWeek = this.findFiscalWeekForDate(today);
    if (!currentWeek) return null;

    const lastCompletedWeek = this.findPreviousFiscalWeek(currentWeek);
    if (!lastCompletedWeek) return null;

    const endWeekIndex = this.fiscalWeeks.indexOf(lastCompletedWeek);
    if (endWeekIndex < 0) return null;

    const startIndex = endWeekIndex - 51;
    if (startIndex < 0) {
      // Not enough weeks, use the earliest available
      const firstWeek = this.fiscalWeeks[0];
      if (!firstWeek) return null;
      return {
        startDate: firstWeek.startDate,
        endDate: lastCompletedWeek.endDate
      };
    }

    const targetWeek = this.fiscalWeeks[startIndex];
    if (!targetWeek) return null;

    return {
      startDate: targetWeek.startDate,
      endDate: lastCompletedWeek.endDate
    };
  }

  /**
   * Last Fiscal Month: find the fiscal month containing today,
   * then find the previous one.
   */
  private lastFiscalMonth(today: dayjs.Dayjs): PresetDateRange | null {
    const currentMonth = this.findCurrentFiscalMonth(today);
    if (!currentMonth) return null;

    const previousMonth = this.getPreviousFiscalMonth(currentMonth);
    if (!previousMonth) return null;

    return this.mapToPresetRange(previousMonth);
  }

  /**
   * Last Fiscal Quarter: find today's quarter/year, subtract 1,
   * then look up quarters["{q}-{y}"].
   */
  private lastFiscalQuarter(today: dayjs.Dayjs): PresetDateRange | null {
    const currentMonth = this.findCurrentFiscalMonth(today);
    if (!currentMonth) return null;

    let prevQuarter = currentMonth.quarter - 1;
    let prevYear = currentMonth.year;
    if (prevQuarter < 1) {
      prevQuarter = 4;
      prevYear -= 1;
    }

    return this.lookupQuarter(prevQuarter, prevYear);
  }

  private lastFiscalYear(today: dayjs.Dayjs): PresetDateRange | null {
    const currentMonth = this.findCurrentFiscalMonth(today);
    if (!currentMonth) return null;

    return this.lookupYear(currentMonth.year - 1);
  }

  private thisFiscalWeek(today: dayjs.Dayjs): PresetDateRange | null {
    const week = this.findFiscalWeekForDate(today);
    if (!week) return null;

    return { startDate: week.startDate, endDate: week.endDate };
  }

  /**
   * This Fiscal Month: find the fiscal month where today falls between startDate and endDate.
   */
  private thisFiscalMonth(today: dayjs.Dayjs): PresetDateRange | null {
    const month = this.findCurrentFiscalMonth(today);
    if (!month) return null;

    return this.mapToPresetRange(month);
  }

  /**
   * This Fiscal Quarter: find today's quarter/year and look up quarters["{q}-{y}"].
   */
  private thisFiscalQuarter(today: dayjs.Dayjs): PresetDateRange | null {
    const currentMonth = this.findCurrentFiscalMonth(today);
    if (!currentMonth) return null;

    return this.lookupQuarter(currentMonth.quarter, currentMonth.year);
  }

  private thisFiscalYear(today: dayjs.Dayjs): PresetDateRange | null {
    const currentMonth = this.findCurrentFiscalMonth(today);
    if (!currentMonth) return null;

    return this.lookupYear(currentMonth.year);
  }

  /**
   * Previous Period: shift the current range backward by its duration.
   * newEnd = currentStart - 1 day, newStart = newEnd - duration.
   */
  private previousPeriod(currentRange: PresetDateRange): PresetDateRange {
    const duration = currentRange.endDate.diff(currentRange.startDate, 'day');
    const newEnd = currentRange.startDate.subtract(1, 'day');
    const newStart = newEnd.subtract(duration, 'day');

    return { startDate: newStart, endDate: newEnd };
  }

  /**
   * Previous Period (match day of week): shift by the period duration,
   * then adjust both start and end backward so the start weekday matches.
   */
  private previousPeriodMatchDow(
    currentRange: PresetDateRange
  ): PresetDateRange {
    const previousDate = this.previousPeriod(currentRange);
    return this.adjustToMatchWeekday(currentRange, previousDate, true);
  }

  /**
   * Previous Fiscal Year: subtract 1 year from both start and end,
   * matching the date-range-picker's `calculatePrevious(date, 'year')`.
   */
  private previousFiscalYear(currentRange: PresetDateRange): PresetDateRange {
    return {
      startDate: currentRange.startDate.subtract(1, 'year'),
      endDate: currentRange.endDate.subtract(1, 'year')
    };
  }

  /**
   * Previous Fiscal Year (match day of week): subtract 1 year, then
   * adjust both start and end so the start weekday matches (nearest direction).
   */
  private previousFiscalYearMatchDow(
    currentRange: PresetDateRange
  ): PresetDateRange {
    const previousDate = this.previousFiscalYear(currentRange);
    return this.adjustToMatchWeekday(currentRange, previousDate, false);
  }

  private toUtcDay(date: string): dayjs.Dayjs {
    return dayjs(date).utc(true);
  }

  private mapToPresetRange(range: {
    startDate: string;
    endDate: string;
  }): PresetDateRange {
    return {
      startDate: this.toUtcDay(range.startDate),
      endDate: this.toUtcDay(range.endDate)
    };
  }

  private lookupQuarter(quarter: number, year: number): PresetDateRange | null {
    const data = this.calendarData?.quarters[`${quarter}-${year}`];
    return data ? this.mapToPresetRange(data) : null;
  }

  private lookupYear(year: number): PresetDateRange | null {
    const data = this.calendarData?.years[`${year}`];
    return data ? this.mapToPresetRange(data) : null;
  }

  private isDateInRange(
    date: dayjs.Dayjs,
    start: dayjs.Dayjs,
    end: dayjs.Dayjs
  ): boolean {
    return !date.isBefore(start, 'day') && !date.isAfter(end, 'day');
  }

  /**
   * Splits each month's date range into 7-day chunks.
   * weekNumber from the month is used as the starting week offset.
   */
  private deriveWeeks(months: FiscalMonthDTO[]): FiscalWeek[] {
    const weeks: FiscalWeek[] = [];

    for (const month of months) {
      const monthStart = this.toUtcDay(month.startDate);
      const monthEnd = this.toUtcDay(month.endDate);
      let weekStart = monthStart;
      let weekNum = month.weekNumber;

      while (
        weekStart.isBefore(monthEnd, 'day') ||
        weekStart.isSame(monthEnd, 'day')
      ) {
        let weekEnd = weekStart.add(6, 'day');

        // Clamp the end of the last week to the month boundary
        if (weekEnd.isAfter(monthEnd, 'day')) {
          weekEnd = monthEnd;
        }

        weeks.push({
          weekNumber: weekNum,
          year: month.year,
          startDate: weekStart,
          endDate: weekEnd
        });

        weekNum++;
        weekStart = weekEnd.add(1, 'day');
      }
    }

    return weeks;
  }

  /**
   * Find the fiscal month containing the given date.
   */
  private findCurrentFiscalMonth(
    date: dayjs.Dayjs
  ): FiscalMonthDTO | undefined {
    if (!this.calendarData) return undefined;

    return this.calendarData.months.find((m) =>
      this.isDateInRange(
        date,
        this.toUtcDay(m.startDate),
        this.toUtcDay(m.endDate)
      )
    );
  }

  private findFiscalWeekForDate(date: dayjs.Dayjs): FiscalWeek | undefined {
    return this.fiscalWeeks.find((w) =>
      this.isDateInRange(date, w.startDate, w.endDate)
    );
  }

  private findPreviousFiscalWeek(
    currentWeek: FiscalWeek
  ): FiscalWeek | undefined {
    const index = this.fiscalWeeks.indexOf(currentWeek);
    return index > 0 ? this.fiscalWeeks[index - 1] : undefined;
  }

  private getPreviousFiscalMonth(
    currentMonth: FiscalMonthDTO
  ): FiscalMonthDTO | undefined {
    let prevMonthNum = currentMonth.month - 1;
    let prevYear = currentMonth.year;
    if (prevMonthNum < 1) {
      prevMonthNum = 12;
      prevYear -= 1;
    }

    return this.findFiscalMonth(prevMonthNum, prevYear);
  }

  private findFiscalMonth(
    month: number,
    year: number
  ): FiscalMonthDTO | undefined {
    return this.calendarData?.months.find(
      (m) => m.month === month && m.year === year
    );
  }

  /**
   * Return the smallest absolute adjustment (forward or backward) to get
   * from `currentWeekday` to `targetWeekday`.
   * Positive = shift forward, negative = shift backward.
   */
  private getNearestDayAdjustment(
    targetWeekday: number,
    currentWeekday: number
  ): number {
    const forwardDiff = (targetWeekday - currentWeekday + 7) % 7;
    const backwardDiff = (currentWeekday - targetWeekday + 7) % 7;

    return backwardDiff < forwardDiff ? -backwardDiff : forwardDiff;
  }

  /**
   * Shifts previousRange so its start weekday matches currentRange's start weekday.
   * backwardOnly=true always shifts backward (previous-period).
   * backwardOnly=false picks the nearest direction (previous-year).
   */
  private adjustToMatchWeekday(
    currentRange: PresetDateRange,
    previousRange: PresetDateRange,
    backwardOnly: boolean
  ): PresetDateRange {
    const currentStartDow = currentRange.startDate.day();
    const previousStartDow = previousRange.startDate.day();

    if (currentStartDow === previousStartDow) {
      return previousRange;
    }

    if (backwardOnly) {
      const dayAdjustment = (previousStartDow - currentStartDow + 7) % 7;
      return {
        startDate: previousRange.startDate.subtract(dayAdjustment, 'day'),
        endDate: previousRange.endDate.subtract(dayAdjustment, 'day')
      };
    }

    const dayAdjustment = this.getNearestDayAdjustment(
      currentStartDow,
      previousStartDow
    );

    return {
      startDate: previousRange.startDate.add(dayAdjustment, 'day'),
      endDate: previousRange.endDate.add(dayAdjustment, 'day')
    };
  }
}
